import React, { useEffect, useReducer, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Modal, {
  ModalBody,
  ModalCustomContent,
} from '../../components/modal/Modal';
import MoreViewsMenu from './MoreViewsMenu';
import _ from 'lodash';
import { SvgIcon } from '../../../core/icons';
import MoreViewsVisualizer from './MoreViewsVisualizer';
import { combineClassNames } from '../../../core/css';
import LegalDisclosure from '../../components/legalDisclosure/LegalDisclosure';
import { useTranslate } from '../../../core/translate';

// TODO: Get translation labels.
const CONTROLS_MAP = {
  hasRoof: {
    labels: { trueLbl: 'roof.off.v3', falseLbl: 'roof.on.v3' },
    messageAction: 'toggleRoof',
    icons: { trueIcn: 'roof-toggle', falseIcn: 'roof-toggle' },
    navOrder: 2,
  },
  hasInterior: {
    labels: { trueLbl: 'view.interior.v3', falseLbl: 'view.exterior.v3' },
    messageAction: 'toggleView',
    icons: {
      trueIcn: 'interior-toggle',
      falseIcn: 'exterior-toggle',
    },
    navOrder: 1,
  },
  hasHeadlights: {
    labels: { trueLbl: 'headlights.off.v3', falseLbl: 'headlights.on.v3' },
    messageAction: 'toggleHeadlights',
    icons: {
      trueIcn: 'headlights-off-toggle',
      falseIcn: 'headlights-on-toggle',
    },
    navOrder: 3,
  },
  hasDarkMode: {
    labels: { trueLbl: 'light.mode.v3', falseLbl: 'dark.mode.v3' },
    messageAction: 'toggleEnvironmentLights',
    icons: { trueIcn: 'dark-mode-on-toggle', falseIcn: 'dark-mode-off-toggle' },
    navOrder: 4,
  },
};

const TOGGLE_MAP = {
  hasInterior: 'exterior',
  hasHeadlights: 'headlightsOn',
  hasRoof: 'roofOn',
  hasDarkMode: 'useDarkEnvironment',
};

const INITIAL_STATE = {
  hasRoof: false,
  hasInterior: true,
  hasHeadlights: true,
  hasDarkMode: false,
};

const controlReducer = (state, { type, payload }) => {
  let controlIndex = 0;
  let controlObj = null;
  switch (type) {
    case 'toggle':
      controlIndex = state.findIndex(
        (controlItem) => controlItem.messageAction === payload
      );
      controlObj = state[controlIndex];
      if (controlObj) {
        return Object.assign([], state, {
          [controlIndex]: {
            ...controlObj,
            isToggled: !controlObj.isToggled,
          },
        });
      }
      return state;
    case 'init':
      return Object.entries(payload)
        .filter(([_key, value]) => value)
        .reduce((accum, [key, _value]) => {
          if (CONTROLS_MAP[key]) {
            const { labels, messageAction, icons, navOrder } = CONTROLS_MAP[
              key
            ];
            return [
              ...accum,
              {
                key,
                labels,
                messageAction,
                isToggled: INITIAL_STATE[key],
                icons,
                navOrder,
              },
            ];
          } else {
            return accum;
          }
        }, [])
        .sort((a, b) => a.navOrder - b.navOrder);
    case 'onThreeDResponse':
      return state
        .map((control) => {
          const controlLbl = TOGGLE_MAP[control.key];
          if (
            typeof payload[controlLbl] !== 'undefined' &&
            payload[controlLbl] !== control.isToggled
          ) {
            return { ...control, isToggled: payload[controlLbl] };
          }
          return control;
        }, [])
        .sort((a, b) => a.navOrder - b.navOrder);
    default:
      return state;
  }
};

const MoreViewsContent = ({ loading, children }) => (
  <>
    {' '}
    {loading && (
      <SvgIcon
        name="spinner"
        className="more-views__spinner svg-icon--option"
      />
    )}
    <div
      className={`more-views__visualizer-container${
        loading ? ' more-views__visualizer-container--hidden' : ''
      }`}>
      {children}
    </div>
  </>
);

const MoreViewsThreeSixtyIcon = ({ hidden, isExteriorView }) => {
  const getThreeSixtyClasses = () => {
    let classString = 'more-views__threesixty-icon';
    if (!isExteriorView) {
      classString = `${classString} more-views__threesixty-icon--centered`;
    }

    if (hidden) {
      classString = `${classString} more-views__threesixty-icon--hidden`;
    }

    return classString;
  };

  const className = getThreeSixtyClasses();

  return (
    <SvgIcon
      className={className}
      name={isExteriorView ? '360-view' : '360-view-inverted'}
    />
  );
};

const MoreViewsThreeD = ({ cgiBackgroundColor, hidden = true }) => {
  const navigate = useNavigate();
  const [loadingVisualizer, setLoadingVisualizer] = useState(true);
  const [controlState, dispatchControlAction] = useReducer(controlReducer, []);
  const [previousConfig, setPreviousConfig] = useState(null);
  const [hideDisplayIcon, setHideDisplayIcon] = useState(false);
  const [isThreeDExperience, setIsThreeDExperience] = useState(false);
  const [disableControls, setDisableControls] = useState(false);
  const [isMoreViewsPath, setIsMoreViewsPath] = useState(false);
  const visualizerFrame = useRef(null);

  const location = useLocation();
  const search = location.search;
  const pathname = location.pathname;

  const isExteriorViewParam = new URLSearchParams(search).get('isExterior');
  const isExteriorView = isExteriorViewParam === 'true';
  const angle = new URLSearchParams(search).get('angle');

  const translate = useTranslate();
  const closeAlt = translate('byo.page.general.close');

  const sendPostMessage = ({ id, request = '' }) => {
    if (visualizerFrame.current && visualizerFrame.current.src) {
      visualizerFrame.current.contentWindow.postMessage(
        { id, request },
        visualizerFrame.current.src
      );
    }
  };

  const onIframeRpoToggle = (rpo) => {
    sendPostMessage({ id: 'toggleConfig', request: rpo });
  };

  const onToggle = (action) => {
    sendPostMessage(action);
    if (!isThreeDExperience) {
      return dispatchControlAction({ type: 'toggle', payload: action.id });
    }
    setDisableControls(true);
  };

  const isAppViewExterior =
    controlState.length > 0 &&
    (controlState.find(({ key }) => key === 'hasInterior') || {}).isToggled;

  useEffect(() => {
    if (isMoreViewsPath && !pathname.includes('moreviews')) {
      setIsMoreViewsPath(false);
    } else {
      if (
        !loadingVisualizer &&
        (!isMoreViewsPath || isThreeDExperience) &&
        pathname.includes('moreviews') &&
        isAppViewExterior !== isExteriorView
      ) {
        onToggle({ id: 'toggleView' });
        setIsMoreViewsPath(true);
      }
    }
  }, [pathname, loadingVisualizer]);

  useEffect(() => {
    const callback = (e) => {
      const { data } = e;

      if (data) {
        const { response, id } = data;
        switch (id) {
          case 'configError':
            break;
          case 'windowClicked':
            if (!hideDisplayIcon) {
              setHideDisplayIcon(true);
            }
            break;
          case 'configToggleMap':
            if (!_.isEqual(response, previousConfig)) {
              setIsThreeDExperience(response.is3d);
              setPreviousConfig(response);
              dispatchControlAction({ type: 'init', payload: response });
            }
            break;
          case 'toggleSuccess':
            setDisableControls(false);
            dispatchControlAction({
              type: 'onThreeDResponse',
              payload: response,
            });
            break;
          case 'configLoading':
            setLoadingVisualizer(response);
            break;
        }
      }
    };
    window.addEventListener('message', callback);
    return () => window.removeEventListener('message', callback);
  }, [previousConfig, hideDisplayIcon]);

  const onClose = () => {
    navigate('.');
  };

  const getAnalyticsKey = () => {
    return `gallery:${isAppViewExterior ? 'exterior' : 'interior'}:${
      isThreeDExperience ? '360' : 'pano'
    }:toggle menu bar`;
  };

  return (
    <Modal
      className={combineClassNames(
        'more-views__modal',
        pathname.includes('moreviews') ? 'more-views__modal--unhide' : ''
      )}
      overlayClassName={combineClassNames(
        'more-views__overlay',
        pathname.includes('moreviews') ? 'more-views__overlay--unhide' : ''
      )}
      bodyOpenClassName="more-views__modal--open"
      isOpen={true}
      onRequestClose={onClose}>
      <ModalCustomContent>
        {({ setIsClosing, onCloseModal }) => (
          <div className="more-views__legal-disclosure">
            <LegalDisclosure isRelative />
            <button
              onClick={() => {
                setIsClosing(true, onCloseModal(onClose));
              }}
              className="more-views__close-btn stat-button-close"
              data-dtm={getAnalyticsKey()}
              aria-label={closeAlt}>
              <SvgIcon
                className="more-views__close-icon"
                name="more-views-close"
              />
            </button>
          </div>
        )}
      </ModalCustomContent>
      <ModalBody innerClassName="more-views__modal-body">
        <MoreViewsContent loading={loadingVisualizer}>
          <MoreViewsVisualizer
            isVisualizer
            background={cgiBackgroundColor}
            imageAngle={angle}
            ref={visualizerFrame}
            onIframeRpoToggle={onIframeRpoToggle}
          />
        </MoreViewsContent>
        <MoreViewsMenu
          onClose={onClose}
          controls={controlState}
          onToggle={onToggle}
          loading={loadingVisualizer}
          disableControls={disableControls}
          analyticsKey={getAnalyticsKey()}
        />
        <MoreViewsThreeSixtyIcon
          hidden={loadingVisualizer || hideDisplayIcon || !isThreeDExperience}
          isExteriorView={isAppViewExterior}
        />
      </ModalBody>
    </Modal>
  );
};

export { controlReducer };
export default MoreViewsThreeD;
